import { Input } from "./Input";
import React, { useState, useEffect } from "react";
import { useDriver } from "./DriverContext";
import { DriverPersonalInfo } from "./types";
import { X, EyeOff, Eye } from "lucide-react";
import { ImageUploader } from "./ImageUploader";

export const PersonalInfoForm: React.FC<{ driverType: string }> = ({
  driverType,
}) => {
  const { state, dispatch } = useDriver();
  const { personalInfo } = state;
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const selectedDriverType = personalInfo.driverType || driverType;

  // Validate all fields whenever personalInfo changes
  useEffect(() => {
    const fieldsToValidate: Array<keyof DriverPersonalInfo> = [
      "name",
      "email",
      "phoneNumber",
      "education",
      "dateOfBirth",
      "address",
      "aadharNumber",
      "username",
      "password",
      "profilePicture",
      "aadharFrontPicture",
      "aadharBackPicture",
    ];

    fieldsToValidate.forEach((field) => {
      if (personalInfo[field] !== undefined && personalInfo[field] !== null) {
        validateField(field, personalInfo[field] as string | File);
      }
    });
  }, [personalInfo]);

  const updatePersonalInfo = (
    field: keyof DriverPersonalInfo,
    value: string
  ) => {
    dispatch({
      type: "UPDATE_PERSONAL_INFO",
      payload: { [field]: value },
    });

    // Mark field as touched when user starts typing
    if (!touched[field]) {
      setTouched((prev) => ({ ...prev, [field]: true }));
    }
  };

  const handleBlur = (field: keyof DriverPersonalInfo) => {
    if (!touched[field]) {
      setTouched((prev) => ({ ...prev, [field]: true }));
    }
  };

  const validateField = (
    field: keyof DriverPersonalInfo,
    value: string | File | null
  ) => {
    let error = "";

    switch (field) {
      case "name":
        if (!value) error = "Name is required";
        break;
      case "email":
        if (!value) {
          error = "Email is required";
        } else if (!/^\S+@\S+\.\S+$/.test(value as string)) {
          error = "Invalid email format";
        }
        break;
      case "phoneNumber":
        if (!value) {
          error = "Phone number is required";
        } else if (!/^\d{10}$/.test(value as string)) {
          error = "Phone number must be 10 digits";
        }
        break;
      case "education":
        if (!value) error = "Education is required";
        break;
      case "dateOfBirth":
        if (!value) error = "Date of birth is required";
        break;
      case "address":
        if (!value) error = "Address is required";
        break;
      case "aadharNumber":
        if (!value) {
          error = "Aadhar number is required";
        } else if (!/^\d{12}$/.test(value as string)) {
          error = "Aadhar number must be 12 digits";
        }
        break;
      case "username":
        if (!value) error = "Username is required";
        break;
      case "password":
        if (!value) {
          error = "Password is required";
        } else if (
          !/(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])/.test(value as string)
        ) {
          error =
            "Password must contain uppercase, number, and special character";
        }
        break;
      case "profilePicture":
        if (!value) error = "Profile picture is required";
        break;
      case "aadharFrontPicture":
        if (!value) error = "Aadhar front picture is required";
        break;
      case "aadharBackPicture":
        if (!value) error = "Aadhar back picture is required";
        break;
      default:
        break;
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
    return !error;
  };

  const handleFileUpload = (field: keyof DriverPersonalInfo, file: File) => {
    dispatch({
      type: "UPDATE_PERSONAL_INFO",
      payload: { [field]: file },
    });
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const handleImageDelete = (field: keyof DriverPersonalInfo) => {
    dispatch({
      type: "UPDATE_PERSONAL_INFO",
      payload: { [field]: null },
    });
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const validateForm = (): boolean => {
    const fieldsToValidate: Array<keyof DriverPersonalInfo> = [
      "name",
      "email",
      "phoneNumber",
      "education",
      "dateOfBirth",
      "address",
      "aadharNumber",
      "username",
      "password",
      "profilePicture",
      "aadharFrontPicture",
      "aadharBackPicture",
    ];

    let isValid = true;
    const newErrors: Record<string, string> = {};

    fieldsToValidate.forEach((field) => {
      const value = personalInfo[field] ?? null;

      let error = "";
      switch (field) {
        case "name":
          if (!value) error = "Name is required";
          break;
        case "email":
          if (!value) {
            error = "Email is required";
          } else if (!/^\S+@\S+\.\S+$/.test(value as string)) {
            error = "Invalid email format";
          }
          break;
        case "phoneNumber":
          if (!value) {
            error = "Phone number is required";
          } else if (!/^\d{10}$/.test(value as string)) {
            error = "Phone number must be 10 digits";
          }
          break;
        case "education":
          if (!value) error = "Education is required";
          break;
        case "dateOfBirth":
          if (!value) error = "Date of birth is required";
          break;
        case "address":
          if (!value) error = "Address is required";
          break;
        case "aadharNumber":
          if (!value) {
            error = "Aadhar number is required";
          } else if (!/^\d{12}$/.test(value as string)) {
            error = "Aadhar number must be 12 digits";
          }
          break;
        case "username":
          if (!value) error = "Username is required";
          break;
        case "password":
          if (!value) {
            error = "Password is required";
          } else if (
            !/(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])/.test(value as string)
          ) {
            error = "Password must contain uppercase, number, special char";
          }
          break;
        case "profilePicture":
          if (!value) error = "Profile picture is required";
          break;
        case "aadharFrontPicture":
          if (!value) error = "Aadhar front picture is required";
          break;
        case "aadharBackPicture":
          if (!value) error = "Aadhar back picture is required";
          break;
      }

      if (error) isValid = false;
      newErrors[field] = error;
    });

    setErrors(newErrors); // update all at once
    return isValid;
  };

  const handleNext = () => {
    // Mark all touched:
    const fields: Array<keyof DriverPersonalInfo> = [
      "name",
      "email",
      "phoneNumber",
      "education",
      "dateOfBirth",
      "address",
      "aadharNumber",
      "username",
      "password",
      "profilePicture",
      "aadharFrontPicture",
      "aadharBackPicture",
    ];
    const newTouched: Record<string, boolean> = {};
    fields.forEach((field) => (newTouched[field] = true));
    setTouched(newTouched);

    const isValid = validateForm();
    console.log("Is Valid:", isValid);

    if (isValid) {
      dispatch({ type: "SET_STEP", payload: "vehicle" });
    }
  };

  return (
    <div className="w-[600px] mx-auto bg-white rounded-lg shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-medium text-green-600">
          ADD DRIVER PERSONAL INFORMATION
        </h2>
        <X
          className="w-5 h-5 text-gray-400 cursor-pointer"
          onClick={() => dispatch({ type: "CLOSE_FORM" })}
        />
      </div>

      <div className="py-4">
        <label>Select Driver type</label>
        <select
          className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-purple-500 ml-2 cursor-pointer"
          value={selectedDriverType}
          onChange={(e) => updatePersonalInfo("driverType", e.target.value)}
        >
          <option value="driver">Delivery Boy</option>
          <option value="pickup">Sample Pick Up Person</option>
        </select>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Profile Picture <span className="text-red-500">*</span>
        </label>
        <ImageUploader
          label=""
          image={personalInfo.profilePicture ?? null}
          inputId="profile-image"
          onUpload={(file) => handleFileUpload("profilePicture", file)}
          onDelete={() => handleImageDelete("profilePicture")}
          hasError={!!errors.profilePicture && touched.profilePicture}
        />
        {errors.profilePicture && touched.profilePicture && (
          <p className="text-red-500 text-xs mt-1">{errors.profilePicture}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Input
            label="Name"
            value={personalInfo.name}
            onChange={(value) => updatePersonalInfo("name", value)}
            onBlur={() => handleBlur("name")}
            placeholder="Enter Name"
            required
            hasError={!!errors.name && touched.name}
          />
          {errors.name && touched.name && (
            <p className="text-red-500 text-xs mt-1">{errors.name}</p>
          )}
        </div>
        <div>
          <Input
            label="Email"
            value={personalInfo.email}
            onChange={(value) => updatePersonalInfo("email", value)}
            onBlur={() => handleBlur("email")}
            placeholder="Enter Email"
            type="email"
            required
            hasError={!!errors.email && touched.email}
          />
          {errors.email && touched.email && (
            <p className="text-red-500 text-xs mt-1">{errors.email}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Input
            label="Education"
            value={personalInfo.education}
            onChange={(value) => updatePersonalInfo("education", value)}
            onBlur={() => handleBlur("education")}
            placeholder="Enter Education"
            required
            hasError={!!errors.education && touched.education}
          />
          {errors.education && touched.education && (
            <p className="text-red-500 text-xs mt-1">{errors.education}</p>
          )}
        </div>
        <div>
          <Input
            label="Phone number"
            value={personalInfo.phoneNumber}
            onChange={(value) => {
              if (/^\d{0,10}$/.test(value)) {
                updatePersonalInfo("phoneNumber", value);
              }
            }}
            onBlur={() => handleBlur("phoneNumber")}
            placeholder="Enter Phone Number"
            type="tel"
            required
            hasError={!!errors.phoneNumber && touched.phoneNumber}
          />
          {errors.phoneNumber && touched.phoneNumber && (
            <p className="text-red-500 text-xs mt-1">{errors.phoneNumber}</p>
          )}
        </div>
      </div>

      <div>
        <Input
          label="Date of Birth"
          value={personalInfo.dateOfBirth}
          onChange={(value) => updatePersonalInfo("dateOfBirth", value)}
          onBlur={() => handleBlur("dateOfBirth")}
          placeholder="Enter DOB"
          type="date"
          required
          hasError={!!errors.dateOfBirth && touched.dateOfBirth}
        />
        {errors.dateOfBirth && touched.dateOfBirth && (
          <p className="text-red-500 text-xs mt-1">{errors.dateOfBirth}</p>
        )}
      </div>

      <div>
        <Input
          label="Address"
          value={personalInfo.address}
          onChange={(value) => updatePersonalInfo("address", value)}
          onBlur={() => handleBlur("address")}
          placeholder="Enter Address"
          required
          hasError={!!errors.address && touched.address}
        />
        {errors.address && touched.address && (
          <p className="text-red-500 text-xs mt-1">{errors.address}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Aadhar Front Picture <span className="text-red-500">*</span>
          </label>
          <ImageUploader
            label=""
            image={personalInfo.aadharFrontPicture ?? null}
            inputId="aadhar-front-image"
            onUpload={(file) => handleFileUpload("aadharFrontPicture", file)}
            onDelete={() => handleImageDelete("aadharFrontPicture")}
            hasError={!!errors.aadharFrontPicture && touched.aadharFrontPicture}
          />
          {errors.aadharFrontPicture && touched.aadharFrontPicture && (
            <p className="text-red-500 text-xs mt-1">
              {errors.aadharFrontPicture}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Aadhar Back Picture <span className="text-red-500">*</span>
          </label>
          <ImageUploader
            label=""
            image={personalInfo.aadharBackPicture ?? null}
            inputId="aadhar-back-image"
            onUpload={(file) => handleFileUpload("aadharBackPicture", file)}
            onDelete={() => handleImageDelete("aadharBackPicture")}
            hasError={!!errors.aadharBackPicture && touched.aadharBackPicture}
          />
          {errors.aadharBackPicture && touched.aadharBackPicture && (
            <p className="text-red-500 text-xs mt-1">
              {errors.aadharBackPicture}
            </p>
          )}
        </div>
      </div>

      <div>
        <Input
          label="Aadhar number"
          value={personalInfo.aadharNumber}
          onChange={(value) => {
            if (/^\d{0,12}$/.test(value)) {
              updatePersonalInfo("aadharNumber", value);
            }
          }}
          onBlur={() => handleBlur("aadharNumber")}
          placeholder="Enter aadhar number"
          type="tel"
          required
          hasError={!!errors.aadharNumber && touched.aadharNumber}
        />
        {errors.aadharNumber && touched.aadharNumber && (
          <p className="text-red-500 text-xs mt-1">{errors.aadharNumber}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4 border-b-1 border-gray-400 bg-gray-200/40 p-2 rounded-lg">
        <div className="space-y-2">
          <label
            htmlFor="username"
            className="block text-sm text-gray-600 font-medium"
          >
            User Name <span className="text-red-500">*</span>
          </label>
          <input
            id="username"
            name="username"
            type="text"
            value={personalInfo.username}
            onChange={(e) => updatePersonalInfo("username", e.target.value)}
            onBlur={() => handleBlur("username")}
            className={`w-full px-3 py-2 border ${
              errors.username && touched.username
                ? "border-red-500"
                : "border-gray-300"
            } outline-none rounded-lg focus:ring-1 focus:ring-emerald-500`}
            placeholder="Enter username"
          />
          {errors.username && touched.username && (
            <p className="text-red-500 text-xs mt-1">{errors.username}</p>
          )}
        </div>
        <div className="space-y-2 relative">
          <label
            htmlFor="password"
            className="block text-sm text-gray-600 font-medium"
          >
            Password <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              value={personalInfo.password}
              onChange={(e) => updatePersonalInfo("password", e.target.value)}
              onBlur={() => handleBlur("password")}
              className={`w-full px-3 py-2 border ${
                errors.password && touched.password
                  ? "border-red-500"
                  : "border-gray-300"
              } outline-none rounded-lg focus:ring-1 focus:ring-emerald-500 pr-10`}
              placeholder="Enter password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute inset-y-0 right-3 flex items-center text-gray-500"
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          {errors.password && touched.password ? (
            <p className="text-red-500 text-xs mt-1">{errors.password}</p>
          ) : (
            <span className="text-xs text-gray-500">
              Password must be contain at least one uppercase letter (A-Z), one
              number (0-9), and one special character (e.g., @, #, $, %).
            </span>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-3 mt-6">
        <button
          className="px-4 py-2 text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
          onClick={() => dispatch({ type: "CLOSE_FORM" })}
        >
          Cancel
        </button>
        <button
          onClick={() => {
            console.log("Form validation state:", validateForm());
            handleNext();
          }}
          className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          Next
        </button>
      </div>
    </div>
  );
};
